import {
  Fragment, useEffect, useState, forwardRef,
} from 'react';
import {
  Transition, Menu, MenuButton, MenuItem, MenuItems,
} from '@headlessui/react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Cog6ToothIcon, GlobeAltIcon, ShoppingBagIcon, ArrowRightStartOnRectangleIcon, UserCircleIcon,
} from '@heroicons/react/24/outline';
import logo from '../../assets/images/logo.svg';
import { AppDispatch, RootState } from '../../redux/store';
import { getProfile } from '../../redux/profile';
import { logout } from '../../redux/auth';
import Cart from '../../views/cart';
import useCartActions from '../../hooks/use-cart-actions';

const Header = forwardRef<HTMLElement>((props, ref) => {
  const { locale } = useParams();
  const navigate = useNavigate();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const profile = useSelector((state: RootState) => state.profile.data);
  const authenticated = useSelector((state: RootState) => state.auth.authenticated);
  const itemCount = useSelector((state: RootState) => state.cart.itemCount);
  const { getCart } = useCartActions();
  const dispatch = useDispatch<AppDispatch>();
  const { i18n, t } = useTranslation();

  useEffect(() => {
    if (authenticated) {
      dispatch(getProfile());
    }
  }, [dispatch, authenticated]);

  useEffect(() => {
    getCart();
  }, []);

  useEffect(() => {
    const handleOpenCartModal = () => {
      setIsCartOpen(true);
    };
    window.addEventListener('openCartModal' as any, handleOpenCartModal);
    return () => {
      window.removeEventListener('openCartModal' as any, handleOpenCartModal);
    };
  }, []);

  const logoutUser = () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return;
    const data = {
      client_id: 'chaizer-webstore',
      client_secret: 'chaizer',
      token_type_hint: 'refresh_token',
      token: refreshToken,
    };
    dispatch(logout(data));
  };

  const switchLanguage = () => {
    const newLocale = locale === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLocale).then(() => {
      localStorage.setItem('locale', newLocale);

      const currentPath = window.location.pathname;
      const newPath = currentPath.replace(/^\/app\/[a-z]{2}/, `/${newLocale}`);
      navigate(newPath, { replace: true });

      document.documentElement.lang = newLocale;
      const newDir = newLocale === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.dir = newDir;
    });
  };
  return (
    <>
      <header
        className='fixed top-0 w-full bg-white/80 backdrop-blur-sm border-b border-gray-200 z-50'
        ref={ref}
      >
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex items-center justify-between h-16'>
            <div className='flex-shrink-0'>
              <Link to={`https://chaizer.com/${locale}`} className='flex items-center space-x-2 rtl:space-x-reverse'>
                <img className='block h-7 w-auto' src={logo} alt='Chaizer' />
              </Link>
            </div>
            <div className='flex items-center space-x-2 sm:space-x-3 rtl:space-x-reverse'>
              <button
                type='button'
                onClick={switchLanguage}
                className='p-2 rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100 focus:outline-none transition-colors'
                aria-label='Switch Language'
              >
                <GlobeAltIcon className='h-6 w-6' />
              </button>

              <div className='relative'>
                <button
                  type='button'
                  onClick={() => setIsCartOpen(true)}
                  className='p-2 rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100 focus:outline-none transition-colors'
                >
                  <ShoppingBagIcon className='h-6 w-6' />
                  {itemCount > 0 && (
                    <span className='absolute top-0 right-0 flex items-center justify-center h-5 w-5 bg-red-500 text-white text-xs font-bold rounded-full'>
                      {itemCount}
                    </span>
                  )}
                </button>
              </div>

              <div className='relative'>
                {authenticated ? (
                  <Menu as='div' className='relative'>
                    <MenuButton className='flex text-sm rounded-full focus:outline-none'>
                      {profile?.profile_picture_url ? (
                        <img
                          className='h-9 w-9 rounded-full object-cover'
                          src={profile.profile_picture_url}
                          alt='User avatar'
                        />
                      ) : (
                        <UserCircleIcon className='h-9 w-9 rounded-full text-gray-500' />
                      )}
                    </MenuButton>
                    <Transition
                      as={Fragment}
                      enter='transition ease-out duration-100'
                      enterFrom='transform opacity-0 scale-95'
                      enterTo='transform opacity-100 scale-100'
                      leave='transition ease-in duration-75'
                      leaveFrom='transform opacity-100 scale-100'
                      leaveTo='transform opacity-0 scale-95'
                    >
                      <MenuItems className='origin-top-right absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-48 rounded-lg shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10'>
                        <MenuItem>
                          {({ active }) => (
                            <Link
                              to={`/${locale}/account/update`}
                              className={`flex items-center gap-3 px-4 py-2 text-sm text-gray-700 transition-colors ${active ? 'bg-gray-100' : ''}`}
                            >
                              <Cog6ToothIcon className='h-4 w-4' />
                              <span>{t('header.settings')}</span>
                            </Link>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ active }) => (
                            <button
                              type='button'
                              onClick={logoutUser}
                              className={`w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-red-500 transition-colors ${active ? 'bg-gray-100' : ''}`}
                            >
                              <ArrowRightStartOnRectangleIcon className='h-4 w-4' />
                              <span>{t('header.logout')}</span>
                            </button>
                          )}
                        </MenuItem>
                      </MenuItems>
                    </Transition>
                  </Menu>
                ) : (
                  <Link
                    to={`/${locale}/auth/login`}
                    className='rounded-md px-3 sm:px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-lime-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lime-600 transition-colors'
                  >
                    {t('header.login')}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      <Cart isOpen={isCartOpen} toggleDrawer={setIsCartOpen} />
    </>
  );
});

export default Header;
